import { useLayoutEffect, useMemo, useState } from 'react'
import { Expression, ExpressionBinaryOperator } from '~/prix/types/expression'
import FiltersComponent from './viewSelectModalVariable.filters.component'
import styled from 'styled-components'
import { Button } from '~/prix/react/components/button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons'
import { buildFilterPropertyOptions, FilterableByShape } from './filterableByField.util'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: fit-content;
  width: 100%;

  h3 {
    font-size: 0.8rem;
    color: ${({ theme }) => theme.colors.darkGrey};
    font-weight: 600;
    margin-left: 2px;
  }
`

const ButtonAddExpression = styled(Button)`
  font-size: 0.85rem;
  font-weight: 600;
  width: 150px;
  margin-top: 0.3rem;
  box-sizing: border-box;

  @media (max-width: 1028px) {
    width: 12vw;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}px) {
    width: 40vw;
    margin-bottom: 20px;
    margin: 0 auto;
  }
`

const ExpandableHeader = styled.div`
  display: flex;
  flex-direction: column;
  cursor: pointer;
  gap: 0.3rem;

  .header {
    display: flex;
    align-items: center;
    gap: 0.3rem;
  }

  .expandable-line {
    flex-grow: 1;
    border: none;
    border-top: ${({ theme }) => theme.colors.mediumGrey} 1px solid;
  }

  .expandable-arrow {
    margin-left: 8px;
  }

  .expressions {
    cursor: default;
  }
`

interface ExpressionsContainerProps {
  filtersApplied: Expression[] | undefined
  getFilter: (expression: Record<number, Expression> | undefined, index: string) => void
  isLoading: boolean
  selectedVariableIsFilterableBy: FilterableByShape | undefined
  resizeHeight: () => void
}

const createInitialExpression = () => ({
  kind: 'binary' as 'binary',
  type: 'expression' as 'expression',
  left: {} as Expression<string>,
  operator: {} as ExpressionBinaryOperator,
  right: {} as Expression<string>,
})

const ExpressionsContainer = ({
  filtersApplied,
  getFilter,
  isLoading,
  selectedVariableIsFilterableBy,
  resizeHeight,
}: ExpressionsContainerProps) => {
  const [canAddNewExpression, setCanAddNewExpression] = useState(false)
  const [isExpanded, setIsExpanded] = useState(true)

  const possibleProperties = useMemo(
    () => buildFilterPropertyOptions(selectedVariableIsFilterableBy),
    [selectedVariableIsFilterableBy],
  )

  const [expressions, setExpressions] = useState<Record<number, Expression> | undefined>(() => {
    if (filtersApplied?.length && filtersApplied?.length > 0) {
      const filtersArray = filtersApplied.reduce(
        (acc, expression, index) => {
          acc[index] = expression
          return acc
        },
        {} as Record<number, Expression>,
      )
      setCanAddNewExpression(filtersApplied.length >= 3 ? false : true)
      return filtersArray
    }
    return {
      0: createInitialExpression(),
    }
  })

  useLayoutEffect(() => {
    resizeHeight()
  }, [expressions, isExpanded])

  const handleNewExpression = (expression: Expression, index: number) => {
    if (!expressions) return
    const updateExpression = { ...expressions[index] }
    if (updateExpression.kind === 'binary' && expression.kind === 'binary') {
      updateExpression.left = expression.left as Expression
      updateExpression.operator = expression.operator
      updateExpression.right = expression.right as Expression
    }
    const withIndex = { [index]: updateExpression }
    const updatedExpressions = { ...expressions, ...withIndex }

    setExpressions(updatedExpressions)
    setCanAddNewExpression(true)
    getFilter(updatedExpressions, index.toString())

    if (expression.kind === 'binary' && !expression.left) {
      setCanAddNewExpression(false)
    }
  }

  const removeExpression = (index: string) => {
    if (!expressions) return

    const { [Number(index)]: _, ...remainingExpressions } = expressions

    const reindexedExpressions: Record<number, Expression> = {}
    let newIndex = 0

    Object.values(remainingExpressions).forEach(expression => {
      reindexedExpressions[newIndex] = expression
      newIndex++
    })

    setExpressions(reindexedExpressions)
    getFilter(reindexedExpressions, index)

    setCanAddNewExpression(true)
  }

  const addNewExpression = () => {
    if (!expressions) return
    const newIndex = Object.keys(expressions).length
    const newExpression: Expression = createInitialExpression()
    setExpressions({ ...expressions, [newIndex]: newExpression })
    setCanAddNewExpression(false)
  }

  const toggleExpand = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <Container>
      <ExpandableHeader>
        <div className='header'>
          <h3>Filtrar por</h3>
          <hr className='expandable-line' />
          <span
            className='expandable-arrow'
            onClick={e => {
              e.stopPropagation()
              toggleExpand()
            }}
          >
            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
          </span>
        </div>
        {isExpanded && (
          <>
            <div className='expressions'>
              {expressions &&
                Object.entries(expressions).map(([index, expression]) => {
                  return (
                    <FiltersComponent
                      key={index}
                      expressionIndex={index}
                      possibleProperties={possibleProperties}
                      isLoading={isLoading}
                      filtersApplied={expression}
                      setNewExpression={handleNewExpression}
                      removeExpression={removeExpression}
                      resizeHeight={resizeHeight}
                    />
                  )
                })}
            </div>
            <ButtonAddExpression onClick={addNewExpression} disabled={!canAddNewExpression}>
              + Filtros
            </ButtonAddExpression>
          </>
        )}
      </ExpandableHeader>
    </Container>
  )
}

export default ExpressionsContainer
